import { useQuery } from '@apollo/client';
import { css } from '@emotion/css';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import introBackground from '@volumental/assets/images/scanner/IntroPage.png';
import {
  Button,
  Container,
  Content,
  ErrorText,
  Headline,
  isMobileDevice,
  Loader,
  ScanList,
  Text,
  TextButton,
} from '@volumental/common';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FitSessionType } from '../../api/gql/graphql';
import { GetScansQuery } from '../../api/queries/scan-query';
import { cancelOtpSession, logout } from '../../auth';
import { Header } from '../../components/accessible-components';
import { uiConfig } from '../../services/config';
import {
  useHistoryStore,
  useInputStore,
  useScannerStore,
  useSessionStore,
  useViewStore,
} from '../../store';
import {
  CurrentView,
  type ScanListItem as Scan,
  UserFlowType,
} from '../../types/types';
import { QrCode } from '../scanner/components/qr-code';

const footerContentStyles = css`
  position: relative;
  display: flex;
  flex: auto;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  padding: 0 24px;
  margin: 10px 0;

  ::before {
    position: absolute;
    content: '';
    z-index: 1;
    top: -32px;
    left: 0;
    width: 100%;
    height: 32px;
    background-image: linear-gradient(
      to top,
      ${uiConfig.styles.colors.white} 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

const scrollArea = css`
  :after {
    z-index: 1;
    content: '';
    position: absolute;
    left: 0;
    top: 56px;
    height: 18px;
    width: 100%;
    background-image: linear-gradient(
      to bottom,
      ${uiConfig.styles.colors.white} 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

interface SelectScanProps {
  onRestart: () => void;
  onClose: () => void;
}

export function SelectScan({
  onRestart,
  onClose,
}: SelectScanProps): JSX.Element {
  const { t } = useTranslation();
  const { email, scanID, setEmail, setScanID, clear, setPreviousPurchaseID } =
    useInputStore((store) => store);
  const { setFlowType } = useScannerStore((store) => store);
  const { storeFinderUrl } = useSessionStore((store) => store);
  const { clearScans, setScans, scans } = useHistoryStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const [selectedItem, setSelectedItem] = useState<Scan | undefined>();
  const { data, error, loading } = useQuery(GetScansQuery, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const saveAndShowResult = (scan: Scan): void => {
    usageTracker.track(UsageTrackerEvent.SelectScan, {
      scan_id: scan.id,
      scan_type: scan.type,
    });
    setPreviousPurchaseID(scan.previousPurchaseID);
    setScanID(scan.id);
    setFlowType(
      scan.type === 'RetailScan'
        ? UserFlowType.HistoryRScan
        : UserFlowType.HistoryMScan,
    );
    setCurrentView(CurrentView.Result);
  };

  const onChangeEmail = (): void => {
    void logout().then(() => {
      void cancelOtpSession().then(() => {
        clear();
        onRestart();
      });
    });
  };

  useEffect(() => {
    const me = data?.me;
    if (me) {
      clearScans();
      const s: Scan[] = [];
      for (const fitSession of me.fitSessions) {
        if (fitSession.scanID) {
          s.push({
            type:
              fitSession.fitSessionType === FitSessionType.RetailScan
                ? 'RetailScan'
                : 'WebScan',
            id: fitSession.scanID,
            createdAt: fitSession.createdAt as string,
            previousPurchaseID: fitSession.previousPurchaseID ?? undefined,
          });
        }
      }
      if (me.email) {
        setEmail(me.email);
      }
      setSelectedItem(s.find((c) => c.id === scanID) ?? s[0]);
      setScans(s);
      usageTracker.track(UsageTrackerEvent.ShowScanList, {
        scan_count: s.length,
      });
    }
  }, [clearScans, data, scanID, setEmail, setScans]);

  const footerContent = (
    <div className={footerContentStyles}>
      {scans.length > 0 ? (
        <Button
          type="button"
          onClick={() => {
            if (selectedItem?.id) {
              saveAndShowResult(selectedItem);
            }
          }}
          loading={loading}
        >
          {t('button.useThisScan')}
        </Button>
      ) : (
        storeFinderUrl && (
          <Button
            type="button"
            onClick={() => {
              if (storeFinderUrl && window.top) {
                usageTracker.track(UsageTrackerEvent.OpenStoreFinder);
                window.top.location.href = storeFinderUrl;
              }
            }}
          >
            {t('findStore')}
          </Button>
        )
      )}
      <TextButton onClick={onChangeEmail}>
        {t('button.useADifferentEmail')}
      </TextButton>
    </div>
  );

  if (loading) {
    return (
      <Container>
        <Header close={onClose} />
        <Loader />
      </Container>
    );
  }

  return (
    <Container>
      <Header close={onClose} />
      {scans.length === 0 ? (
        <Content>
          <Headline>{t('noScanResults')}</Headline>
          <Text>{t('noScanResultsFound')}</Text>
          {isMobileDevice ? (
            <>
              <img
                src={introBackground}
                alt={t('imageAlt.selectScanIntro')}
                style={{ borderRadius: 24, margin: '30px 0 60px 0' }}
              />
              <Button
                loading={false}
                type="submit"
                onClick={() => {
                  setCurrentView(CurrentView.Intro);
                }}
              >
                {t('button.getStarted')}
              </Button>
            </>
          ) : (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <QrCode
                onScan={(sr) => {
                  saveAndShowResult(sr);
                }}
              />
              <Text>{t('scanTheQRCodeToFindYourBestSize')}</Text>
            </div>
          )}
          <TextButton onClick={onChangeEmail}>
            {t('button.useADifferentEmail')}
          </TextButton>
        </Content>
      ) : (
        <>
          <Content>
            <div className={scrollArea}>
              <Headline>{t('yourScanHistory')}</Headline>
              {Boolean(error) && (
                <ErrorText>{t('somethingWentWrongFinding')}</ErrorText>
              )}
              <Text>
                {`${t('hereAreAllScans')} `}
                <strong>{email}</strong>
                {`. ${t('pleaseSelectAFootScan')}`}
              </Text>
              <ScanList
                items={scans}
                selectedId={selectedItem?.id}
                onSelect={(id: string) => {
                  setSelectedItem(scans.find((scan) => scan.id === id));
                }}
                titles={{
                  instore: t('instoreFootScan'),
                  webscanner: t('onlineFootScan'),
                }}
                selectIconAlt={t('imageAlt.selectScan')}
              />
            </div>
          </Content>
          {footerContent}
        </>
      )}
    </Container>
  );
}
